import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Select, Button, Alert, Spin } from 'antd';
import { components } from 'apiClients/base/schema';
import useSalesChannelOpenApi from 'apiClients/useSalesChannelOpenApiClient';

type ProductBundleDto = components['schemas']['ProductBundleDto'];
type ProductBundleUpdateRequest = components['schemas']['ProductBundleUpdateRequest'];
type SalesChannelResponse = components['schemas']['SalesChannelResponse'];

interface BundleProductEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (id: number, updateData: ProductBundleUpdateRequest) => Promise<void>;
  bundleProduct: ProductBundleDto | null;
}

const BundleProductEditDialog: React.FC<BundleProductEditDialogProps> = ({
  isOpen,
  onClose,
  onUpdate,
  bundleProduct,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<SalesChannelResponse[]>([]);
  const { getSalesChannels, loading: channelsLoading } = useSalesChannelOpenApi();

  useEffect(() => {
    if (isOpen) {
      loadChannels();
      if (bundleProduct) {
        form.setFieldsValue({
          score: bundleProduct.score,
          channelIds: bundleProduct.channelIds,
        });
      }
    }
  }, [isOpen, bundleProduct, form]);

  const loadChannels = async () => {
    try {
      const channelData = await getSalesChannels();
      setChannels(channelData);
    } catch (error) {
      console.error('Failed to load channels:', error);
    }
  };

  const handleSave = async () => {
    if (!bundleProduct) return;

    try {
      setLoading(true);
      const values = await form.validateFields();
      
      const updateData: ProductBundleUpdateRequest = {
        score: values.score,
        channelIds: values.channelIds,
      };

      await onUpdate(bundleProduct.id, updateData);
      onClose();
    } catch (error) {
      console.error('Failed to update bundle product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={`Redigera ${bundleProduct?.name || 'paketprodukt'}`}
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Avbryt
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
        >
          Spara
        </Button>,
      ]}
      width={600}
    >
      {channelsLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            label="Rankingpoäng"
            name="score"
            rules={[
              { required: true, message: 'Rankingpoäng är obligatoriskt' },
              { type: 'number', min: 0, max: 1350, message: 'Rankingpoäng måste vara mellan 0 och 1350' },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={1350}
              placeholder="Ange rankingpoäng"
            />
          </Form.Item>

          <Form.Item
            label="Försäljningskanaler"
            name="channelIds"
            rules={[
              { required: false, message: 'Minst en försäljningskanal måste väljas' },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Välj försäljningskanaler"
              style={{ width: '100%' }}
              options={channels.map(channel => ({
                label: channel.name,
                value: channel.id,
              }))}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default BundleProductEditDialog;
